// src/pages/MarketplaceIntegration/marketplaceService.js

// This service handles all marketplace API integrations
// In a real implementation, each marketplace would have its own API client

class MarketplaceService {
  constructor() {
    // In a real implementation, these would be loaded from environment variables or secure storage
    this.apiConfigs = {
      trendyol: {
        baseUrl: 'https://api.trendyol.com',
        // API credentials would be securely stored
      },
      hepsiburada: {
        baseUrl: 'https://api.hepsiburada.com',
      },
      amazon: {
        baseUrl: 'https://sellingpartnerapi-na.amazon.com',
      },
      n11: {
        baseUrl: 'https://api.n11.com',
      },
      etsy: {
        baseUrl: 'https://openapi.etsy.com',
      }
    };
  }

  // Get products from a marketplace
  async getMarketplaceProducts(marketplaceName, credentials) {
    try {
      switch (marketplaceName.toLowerCase()) {
        case 'trendyol':
          return await this.getTrendyolProducts(credentials);
        case 'hepsiburada':
          return await this.getHepsiburadaProducts(credentials);
        case 'amazon':
          return await this.getAmazonProducts(credentials);
        case 'n11':
          return await this.getN11Products(credentials);
        case 'etsy':
          return await this.getEtsyProducts(credentials);
        default:
          throw new Error(`Unsupported marketplace: ${marketplaceName}`);
      }
    } catch (error) {
      console.error(`Error fetching products from ${marketplaceName}:`, error);
      throw error;
    }
  }

  // Update stock for a product on a marketplace
  async updateMarketplaceStock(marketplaceName, credentials, productData) {
    try {
      // Return success response
      return {
        success: true,
        message: `Stock updated successfully for ${productData.sku} on ${marketplaceName}`,
        updatedProduct: productData
      };
    } catch (error) {
      console.error(`Error updating stock on ${marketplaceName}:`, error);
      throw error;
    }
  }

  // Get orders from a marketplace
  async getMarketplaceOrders(marketplaceName, credentials, filters = {}) {
    try {
      switch (marketplaceName.toLowerCase()) {
        case 'trendyol':
          return await this.getTrendyolOrders(credentials, filters);
        case 'hepsiburada':
          return await this.getHepsiburadaOrders(credentials, filters);
        case 'amazon':
          return await this.getAmazonOrders(credentials, filters);
        case 'n11':
          return await this.getN11Orders(credentials, filters);
        case 'etsy':
          return await this.getEtsyOrders(credentials, filters);
        default:
          throw new Error(`Unsupported marketplace: ${marketplaceName}`);
      }
    } catch (error) {
      console.error(`Error fetching orders from ${marketplaceName}:`, error);
      throw error;
    }
  }

  // Send invoice/invoice data to a marketplace
  async sendInvoiceToMarketplace(marketplaceName, credentials, invoiceData) {
    try {
      // Return success response
      return {
        success: true,
        message: `Invoice sent successfully to ${marketplaceName}`,
        invoiceId: invoiceData.id
      };
    } catch (error) {
      console.error(`Error sending invoice to ${marketplaceName}:`, error);
      throw error;
    }
  }

  // Test marketplace connection
  async testConnection(marketplaceName, credentials) {
    try {
      switch (marketplaceName.toLowerCase()) {
        case 'trendyol':
          return await this.testTrendyolConnection(credentials);
        case 'hepsiburada':
          return await this.testHepsiburadaConnection(credentials);
        case 'amazon':
          return await this.testAmazonConnection(credentials);
        case 'n11':
          return await this.testN11Connection(credentials);
        case 'etsy':
          return await this.testEtsyConnection(credentials);
        default:
          throw new Error(`Unsupported marketplace: ${marketplaceName}`);
      }
    } catch (error) {
      console.error(`Connection test failed for ${marketplaceName}:`, error);
      throw error;
    }
  }

  // Real Trendyol API implementation
  async getTrendyolProducts(credentials) {
    try {
      // Validate credentials
      if (!credentials || !credentials.apiKey || !credentials.apiSecret || !credentials.supplierId) {
        throw new Error('Geçersiz Trendyol API kimlik bilgileri. Tedarikçi ID gereklidir.');
      }

      console.log('Fetching products from Trendyol with credentials:', {
        supplierId: credentials.supplierId,
        apiKey: credentials.apiKey ? '***' + credentials.apiKey.slice(-4) : 'missing',
        apiSecret: credentials.apiSecret ? '***' + credentials.apiSecret.slice(-4) : 'missing'
      });

      const supplierId = credentials.supplierId;
      const targetUrl = `${this.apiConfigs.trendyol.baseUrl}/sapigw/suppliers/${supplierId}/products`;

      // DIRECT API CALL (User has CORS extension)
      console.log('Making request to:', targetUrl);

      const response = await fetch(targetUrl, {
        method: 'GET',
        headers: {
          'User-Agent': `${credentials.supplierId} - SelfIntegration`,
          'Authorization': 'Basic ' + btoa(`${credentials.apiKey}:${credentials.apiSecret}`)
        }
      });

      console.log('Trendyol API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Trendyol API error response:', errorText);
        throw new Error(`Trendyol API hatası: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Trendyol API response data:', data);

      // Handle different possible response structures
      let products = [];

      if (data && Array.isArray(data)) products = data;
      else if (data && data.products && Array.isArray(data.products)) products = data.products;
      else if (data && data.content && Array.isArray(data.content)) products = data.content;
      else if (data && data.data && Array.isArray(data.data)) products = data.data;
      else if (data && data.payload && data.payload.products && Array.isArray(data.payload.products)) products = data.payload.products;
      else {
        console.warn('Trendyol API returned unexpected data structure:', data);
        return [];
      }

      // Transform the data
      const transformedProducts = products.map(product => {
        const id = product.id || product.productId || product.productCode || '';
        const sku = product.sku || product.barcode || product.productCode || '';
        const name = product.title || product.name || product.productName || 'İsimsiz Ürün';
        const price = product.salePrice || product.price || product.listPrice || 0;
        const stock = product.quantity || product.stock || product.availableStock || 0;

        return { id, sku, name, price, stock };
      });

      return transformedProducts;

    } catch (error) {
      console.error('Error fetching products from Trendyol:', error);
      // Don't fallback to mock data automatically, let the user see the error
      throw error;
    }
  }

  async getTrendyolOrders(credentials, filters) {
    try {
      if (!credentials || !credentials.apiKey || !credentials.apiSecret || !credentials.supplierId) {
        throw new Error('Geçersiz Trendyol API kimlik bilgileri. Tedarikçi ID gereklidir.');
      }

      const supplierId = credentials.supplierId;

      // Fetch orders without date filter (let Trendyol use default range) to avoid format issues
      const targetUrl = `${this.apiConfigs.trendyol.baseUrl}/sapigw/suppliers/${supplierId}/orders?orderByField=CreatedDate&orderByDirection=DESC`;

      // DIRECT API CALL (User has CORS extension)
      console.log('Making request to:', targetUrl);

      const response = await fetch(targetUrl, {
        method: 'GET',
        headers: {
          'User-Agent': `${credentials.supplierId} - SelfIntegration`,
          'Authorization': 'Basic ' + btoa(`${credentials.apiKey}:${credentials.apiSecret}`)
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Trendyol API error response:', errorText);
        // Include partial error text in the message so user can see it in Error Logs
        throw new Error(`Trendyol API hatası: ${response.status} - ${response.statusText} - Detay: ${errorText.substring(0, 200)}`);
      }

      const data = await response.json();

      let orders = [];
      if (data && Array.isArray(data)) orders = data;
      else if (data && data.orders && Array.isArray(data.orders)) orders = data.orders;
      else if (data && data.content && Array.isArray(data.content)) orders = data.content;
      else if (data && data.data && Array.isArray(data.data)) orders = data.data;
      else if (data && data.payload && data.payload.orders && Array.isArray(data.payload.orders)) orders = data.payload.orders;
      else return []; // Return empty array if structure is unknown, don't throw error

      const transformedOrders = orders.map(order => {
        const id = order.orderId || order.id || '';
        const orderNumber = order.orderNumber || order.orderCode || id;

        let customer = 'Müşteri Bilgisi Yok';
        if (order.customer) {
          if (typeof order.customer === 'string') customer = order.customer;
          else if (order.customer.firstName || order.customer.lastName) {
            customer = `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim();
          }
        }

        let items = [];
        if (order.orderItems && Array.isArray(order.orderItems)) {
          items = order.orderItems.map(item => ({
            productId: item.productId || item.productCode || '',
            sku: item.productSku || item.barcode || '',
            name: item.productName || item.title || 'Ürün',
            quantity: item.quantity || 0,
            price: item.price || item.salePrice || 0
          }));
        }

        const total = order.totalPrice || order.price || order.amount || 0;
        const status = order.status || 'Bilinmiyor';
        const date = order.orderDate ? new Date(order.orderDate) : new Date();

        return { id, orderNumber, customer, items, total, status, date };
      });

      return transformedOrders;

    } catch (error) {
      console.error('Error fetching orders from Trendyol:', error);
      // Throw error to user instead of mock data
      throw error;
    }
  }

  async testTrendyolConnection(credentials) {
    try {
      if (!credentials || !credentials.apiKey || !credentials.apiSecret || !credentials.supplierId) {
        throw new Error('Geçersiz bilgileri.');
      }

      const supplierId = credentials.supplierId;
      const targetUrl = `${this.apiConfigs.trendyol.baseUrl}/sapigw/suppliers/${supplierId}/products?size=1`;

      // DIRECT API CALL
      const response = await fetch(targetUrl, {
        method: 'GET',
        headers: {
          'User-Agent': `${credentials.supplierId} - SelfIntegration`,
          'Authorization': 'Basic ' + btoa(`${credentials.apiKey}:${credentials.apiSecret}`)
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Trendyol Hatası: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return { success: true, message: 'Bağlantı başarılı (Trendyol API)' };

    } catch (error) {
      console.error('Error testing Trendyol connection:', error);
      throw new Error(`Bağlantı başarısız: ${error.message}`);
    }
  }

  // Marketplace-specific methods (would be implemented with actual API calls)
  async getHepsiburadaProducts(credentials) {
    // Sample data for demo
    return [
      { id: 'H2001', sku: 'HB-001', name: 'Beyaz Gömlek', price: 129.90, stock: 40 },
      { id: 'H2002', sku: 'HB-002', name: 'Gri Kazak', price: 119.90, stock: 25 }
    ];
  }

  async getAmazonProducts(credentials) {
    // Sample data for demo
    return [
      { id: 'A3001', sku: 'AM-001', name: 'Yeşil Elbise', price: 199.90, stock: 15 },
      { id: 'A3002', sku: 'AM-002', name: 'Sarı Şort', price: 79.90, stock: 60 }
    ];
  }

  async getN11Products(credentials) {
    // Sample data for demo
    return [
      { id: 'N4001', sku: 'N11-001', name: 'Lacivert Eşofman', price: 159.90, stock: 35 }
    ];
  }

  async getEtsyProducts(credentials) {
    // Sample data for demo
    return [
      { id: 'E5001', sku: 'ET-001', name: 'El Yapımı Kolye', price: 89.90, stock: 10 },
      { id: 'E5002', sku: 'ET-002', name: 'Ahşap Çerçeve', price: 129.90, stock: 8 }
    ];
  }

  async getHepsiburadaOrders(credentials, filters) {
    // Sample data for demo
    return [
      {
        id: 'HO2001',
        orderNumber: 'HB-ORD-001',
        customer: 'Mehmet Kaya',
        items: [
          { productId: 'H2001', sku: 'HB-001', name: 'Beyaz Gömlek', quantity: 1, price: 129.90 }
        ],
        total: 129.90,
        status: 'pending',
        date: new Date()
      }
    ];
  }

  async getAmazonOrders(credentials, filters) {
    // Sample data for demo
    return [
      {
        id: 'AO3001',
        orderNumber: 'AM-ORD-001',
        customer: 'Ayşe Demir',
        items: [
          { productId: 'A3001', sku: 'AM-001', name: 'Yeşil Elbise', quantity: 1, price: 199.90 }
        ],
        total: 199.90,
        status: 'pending',
        date: new Date()
      }
    ];
  }

  async getN11Orders(credentials, filters) {
    // Sample data for demo
    return [
      {
        id: 'NO4001',
        orderNumber: 'N11-ORD-001',
        customer: 'Fatma Şahin',
        items: [
          { productId: 'N4001', sku: 'N11-001', name: 'Lacivert Eşofman', quantity: 1, price: 159.90 }
        ],
        total: 159.90,
        status: 'pending',
        date: new Date()
      }
    ];
  }

  async getEtsyOrders(credentials, filters) {
    // Sample data for demo
    return [
      {
        id: 'EO5001',
        orderNumber: 'ET-ORD-001',
        customer: 'Elif Öztürk',
        items: [
          { productId: 'E5001', sku: 'ET-001', name: 'El Yapımı Kolye', quantity: 1, price: 89.90 }
        ],
        total: 89.90,
        status: 'pending',
        date: new Date()
      }
    ];
  }

  async testHepsiburadaConnection(credentials) {
    return {
      success: true,
      message: 'Connection successful'
    };
  }

  async testAmazonConnection(credentials) {
    return {
      success: true,
      message: 'Connection successful'
    };
  }

  async testN11Connection(credentials) {
    return {
      success: true,
      message: 'Connection successful'
    };
  }

  async testEtsyConnection(credentials) {
    return {
      success: true,
      message: 'Connection successful'
    };
  }
}

// Export singleton instance
const marketplaceService = new MarketplaceService();
export default marketplaceService;